/**
 * A grade de pinout — o padrão 05 aplicado ao registro central do módulo.
 *
 * Leitura pura: nesta volta ela não escreve nada. A API dessa fatia é a mais
 * pronta que existe (fases 1 e 2 fechadas), então a grade já funciona contra o
 * banco de verdade sem depender de mais nada.
 *
 * Três regras duras aparecem aqui, e não por acaso:
 *  - a página é de 200 e **não se pré-carrega a seguinte** (regra 5);
 *  - filtro de texto aplica no Enter, nunca a cada tecla (regra 2);
 *  - nada revalida sozinho — nem ao voltar o foco na aba (regra 4, no cliente).
 */

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { exportarPinouts, totalDePaginas, usarPinouts } from "@/api/pinouts"
import { POR_PAGINA, chaves } from "@/api/query"
import { ErroDaApi } from "@/api/erros"
import type { PinoutResponse } from "@/api/tipos"
import { obter } from "@/api/cliente"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useEffect } from "react"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { ApagarTodosOsPinouts } from "./ApagarTudo"
import { SeletorDeVisoes } from "@/kit/SeletorDeVisoes"
import { contarFiltros, visaoPadrao, type Visao } from "@/kit/visoes"
import {
  FILTRO_VAZIO, FiltrosPinout, IGNORAR_NA_CONTAGEM, normalizarFiltro, paraApi, type FiltroTela,
} from "./FiltrosPinout"
import { rotuloTipoPinout, tomDoTipo } from "./tipos"
import { Lancamento } from "./lancamento/Lancamento"
import { AcaoEmLote } from "./AcaoEmLote"

const TELA = "pinout"

export function GradePinout({ projetoId }: { projetoId: number }) {
  // A visão marcada como padrão abre junto com a tela — é o que evita remarcar
  // os mesmos seis filtros toda manhã (regra 5 da padronização).
  const inicial = useMemo(() => visaoPadrao<FiltroTela>(TELA, projetoId), [projetoId])

  const [filtro, setFiltro] = useState<FiltroTela>(() => normalizarFiltro(inicial?.filtro))
  const [visaoAtiva, setVisaoAtiva] = useState<string | null>(inicial?.id ?? null)
  const [pagina, setPagina] = useState(1)
  const [selecionada, setSelecionada] = useState<number | null>(null)
  const [lancando, setLancando] = useState(false)
  // A linha em edição. Editar reabre a tela de lançar preenchida — é o que o
  // `Incluir(pinout_id)` do sistema de hoje faz.
  const [editando, setEditando] = useState<PinoutResponse | null>(null)
  // As linhas marcadas que viram pinouts novos pelo lançamento ("Duplicar").
  const [duplicando, setDuplicando] = useState<PinoutResponse[] | null>(null)

  // `?editar=<id>` vem do painel do pinout gráfico: clicou no componente do
  // desenho, escolheu "editar este pinout", cai aqui com a linha aberta. Busca
  // por id em vez de procurar na página — a linha pode estar na página 4.
  const [parametros, setParametros] = useSearchParams()
  useEffect(() => {
    const pedido = Number(parametros.get("editar"))
    if (!pedido) return
    let vivo = true
    obter<PinoutResponse>(`api/pinouts/${pedido}`, {}).then((p) => {
      if (!vivo) return
      setEditando(p)
      setSelecionada(p.id)
    }).catch(() => { /* linha apagada entre o desenho e o clique; segue sem abrir */ })
    // Some da URL: recarregar a página não deve reabrir o formulário.
    parametros.delete("editar")
    setParametros(parametros, { replace: true })
    return () => { vivo = false }
  }, [parametros, setParametros])
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const ir = useNavigate()

  const paraServidor = useMemo(() => paraApi(filtro), [filtro])
  const { data, isPending, isFetching, error, refetch } = usarPinouts(projetoId, paraServidor, pagina)

  const itens = data?.itens ?? []
  const total = data?.total ?? 0

  /**
   * O nome do projeto, que é o que a confirmação de "apagar todos" pede
   * digitado. Vem da mesma consulta que a faixa do projeto usa, então já está
   * em cache — não é uma ida a mais ao servidor.
   */
  const projeto = useQuery({
    queryKey: chaves.projeto(projetoId),
    enabled: projetoId > 0,
    queryFn: () => obter<{ descricao?: string }>(`api/projetos/${projetoId}`),
  })
  const nomeDoProjeto = projeto.data?.descricao?.trim() || `projeto ${projetoId}`

  const [recadoDeManutencao, setRecadoDeManutencao] = useState<string | null>(null)
  const paginas = totalDePaginas(total)
  const primeira = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1
  const ultima = Math.min(pagina * POR_PAGINA, total)
  const quantos = contarFiltros(filtro as unknown as Record<string, unknown>, IGNORAR_NA_CONTAGEM)

  function aplicar(f: FiltroTela, visao: string | null = null) {
    setFiltro(f)
    setVisaoAtiva(visao)
    setPagina(1)   // filtro novo, contagem nova: ficar na página 7 não faria sentido
    // Filtro novo desmarca: manter marcação de linhas que sumiram da vista é
    // exatamente como se altera em lote o que não se queria.
    setMarcados(new Set())
  }

  function marcar(ids: number[], ligar: boolean) {
    setMarcados((m) => {
      const novo = new Set(m)
      for (const id of ids) { if (ligar) novo.add(id); else novo.delete(id) }
      return novo
    })
  }

  return (
    <>
      <div
        className="barra"
        style={{
          height: 36, flex: "0 0 36px", display: "flex", alignItems: "center", gap: 10,
          padding: "0 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <SeletorDeVisoes<FiltroTela>
          tela={TELA}
          escopo={projetoId}
          filtroAtual={filtro}
          temFiltro={quantos}
          ativa={visaoAtiva}
          aoAplicar={(v: Visao<FiltroTela>) => aplicar(normalizarFiltro(v.filtro), v.id)}
          aoLimpar={() => aplicar(FILTRO_VAZIO)}
        />
        {quantos > 0 && (
          <button
            onClick={() => aplicar(FILTRO_VAZIO)}
            style={{ marginLeft: 2, fontSize: 11.5, color: "var(--txt3)" }}
          >
            limpar {quantos} {quantos === 1 ? "filtro" : "filtros"}
          </button>
        )}

        <span style={{ marginLeft: "auto" }} />

        {total > 0 && <BotaoDeExportar projetoId={projetoId} filtro={paraServidor} />}

        {total > 0 && (
          <ApagarTodosOsPinouts
            projetoId={projetoId}
            alvoId={projetoId}
            nomeDoAlvo={nomeDoProjeto}
            aoAvisar={setRecadoDeManutencao}
          />
        )}

        <button
          onClick={() => {
            // O componente da linha marcada (se todas as marcadas são de um só) ou da selecionada.
            const marcadas = itens.filter((l) => marcados.has(l.id))
            const deUm = new Set(marcadas.map((l) => l.componenteId))
            const componente = deUm.size === 1 ? [...deUm][0]
              : marcados.size === 0 ? itens.find((l) => l.id === selecionada)?.componenteId : undefined
            ir(`../grafico?ligacoes=1${componente ? `&componenteId=${componente}` : ""}`)
          }}
          title="Abre o editor de ligações: o pinout como caixas e fios — criar do zero, junção, corrigir"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 11px",
            borderRadius: 7, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--surface)",
            fontSize: 12, fontWeight: 600,
          }}
        >
          Editar ligações
        </button>

        {/* O "Incluir Json" do sistema antigo: abre a aba Importar já no colar JSON. */}
        <button
          onClick={() => ir("../importar?fonte=json")}
          title="Colar o JSON de pinouts, como no Incluir Json do sistema antigo"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 11px",
            borderRadius: 7, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--surface)",
            fontSize: 12, fontWeight: 600,
          }}
        >
          Incluir JSON
        </button>

        {/* Subir apelidos por planilha: abre a aba Importar em Campos em lote (que baixa a planilha). */}
        <button
          onClick={() => ir("../importar?modo=lote")}
          title="Baixar a planilha de apelidos do projeto, preencher a coluna apelido e enviar de volta (Importar › Campos em lote)"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 11px",
            borderRadius: 7, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--surface)",
            fontSize: 12, fontWeight: 600,
          }}
        >
          Apelidos por planilha
        </button>

        {!lancando && (
          <button
            onClick={() => setLancando(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 11px",
              borderRadius: 7, background: "var(--brand)", color: "#FFFFFF",
              fontSize: 12, fontWeight: 600,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
              <path d="M12 5.5v13M5.5 12h13" />
            </svg>
            Lançar pinout
          </button>
        )}
      </div>

      {(lancando || editando || duplicando) && (
        <Lancamento
          projetoId={projetoId}
          pinout={editando}
          duplicar={duplicando}
          aoFechar={() => { setLancando(false); setEditando(null); setDuplicando(null) }}
        />
      )}

      {marcados.size > 0 && (
        <AcaoEmLote
          projetoId={projetoId}
          marcados={marcados}
          linhas={itens}
          aoLimpar={() => setMarcados(new Set())}
          aoDuplicar={(linhas) => {
            setLancando(false); setEditando(null)
            // Na ordem da grade, por pino: é a ordem em que a pessoa conferiu.
            setDuplicando([...linhas])
            // A marcação já virou o lote a duplicar: limpa, para a próxima seleção começar do zero.
            setMarcados(new Set())
          }}
        />
      )}

      <FiltrosPinout projetoId={projetoId} aplicado={filtro} aoAplicar={(f) => aplicar(f)} />

      <Tabela<PinoutResponse>
        colunas={COLUNAS}
        itens={itens}
        chaveDe={(p) => p.id}
        selecionada={selecionada}
        aoSelecionar={(p) => setSelecionada(p.id)}
        aoAbrir={(p) => { setSelecionada(p.id); setLancando(false); setEditando(p) }}
        marcados={marcados}
        aoMarcar={marcar}
        vazio={
          isPending ? (
            <LinhaDeAviso>Carregando os pinouts do projeto…</LinhaDeAviso>
          ) : error ? (
            <LinhaDeAviso>
              <b style={{ color: "var(--erro)" }}>
                {error instanceof ErroDaApi ? error.message : "Não foi possível falar com o servidor."}
              </b>
              <button
                onClick={() => refetch()}
                style={{
                  height: 30, padding: "0 12px", borderRadius: 7, border: "1px solid var(--line2)",
                  background: "var(--surface)", fontSize: 12.5, fontWeight: 600, color: "var(--txt)",
                }}
              >
                Tentar de novo
              </button>
            </LinhaDeAviso>
          ) : quantos > 0 ? (
            <LinhaDeAviso>
              Nenhum pinout com {quantos === 1 ? "esse filtro" : "esses filtros"}.
              <button onClick={() => aplicar(FILTRO_VAZIO)}
                      style={{ fontSize: 12.5, fontWeight: 600, color: "var(--brand-on-soft)" }}>
                Limpar e ver o projeto inteiro
              </button>
            </LinhaDeAviso>
          ) : (
            <LinhaDeAviso>Este projeto ainda não tem pinout nenhum.</LinhaDeAviso>
          )
        }
      />

      {recadoDeManutencao && (
        <p style={{
          margin: 0, padding: "7px 16px", fontSize: 11.5, lineHeight: 1.5,
          background: "var(--surface2)", color: "var(--txt2)",
          borderTop: "1px solid var(--line2)",
        }}>
          {recadoDeManutencao}
        </p>
      )}

      <BarraDeEstado
        direita={<Paginacao pagina={pagina} paginas={paginas} carregando={isFetching} aoIr={setPagina} />}
      >
        {total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{primeira}</b>–
            <b className="num" style={{ color: "var(--txt)" }}>{ultima}</b> de{" "}
            <b className="num" style={{ color: "var(--txt)" }}>{total.toLocaleString("pt-BR")}</b> pinouts
          </span>
        )}
        {filtro.modelos.length > 0 && (
          // RN-01: filtrar por modelo aplica a visibilidade por restrição. Sem
          // dizer isso, some linha e parece dado perdido.
          <span
            title="Filtrar por modelo aplica a restrição (RN-01): pinout restrito a outro modelo não aparece."
            style={{
              display: "inline-flex", alignItems: "center", gap: 5, height: 20, padding: "0 8px",
              borderRadius: 999, background: "var(--acao-soft)", color: "var(--acao-on-soft)",
              fontSize: 10.5, fontWeight: 600,
            }}
          >
            restrição por modelo aplicada
          </span>
        )}
        {marcados.size === 0 && (
          <span style={{ color: "var(--txt3)" }}>
            Enter abre a linha para editar · Espaço marca · Shift+clique marca o intervalo
          </span>
        )}
        {selecionada != null && marcados.size === 0 && (
          <span>Linha <span className="num">{selecionada}</span> selecionada</span>
        )}
      </BarraDeEstado>
    </>
  )
}

// ------------------------------------------------------------------ colunas

const COLUNAS: Coluna<PinoutResponse>[] = [
  {
    chave: "componente",
    rotulo: "Componente",
    minimo: 220,
    // Código e descrição são campos distintos, mas em boa parte do acervo o
    // cadastro repetiu o código na descrição (no projeto 976 são 94 de 139).
    // Desenhar os dois ali daria "EMS BOX EMS BOX" em dois terços das linhas,
    // então a descrição só aparece quando acrescenta alguma coisa.
    render: (p) => {
      const codigo = (p.componenteCodigo ?? "").trim()
      const descricao = (p.componenteDescricao ?? "").trim()
      const repetida = descricao.toLowerCase() === codigo.toLowerCase()
      return (
        <>
          {codigo && (
            <span style={{ fontFamily: "var(--fm)", fontWeight: 600 }}>{codigo}</span>
          )}
          {codigo && descricao && !repetida && " "}
          {!repetida && descricao}
        </>
      )
    },
  },
  { chave: "conector", rotulo: "Conector", largura: 130, mono: true, render: (p) => p.conectorDescricao },
  { chave: "circuito", rotulo: "Circuito", largura: 120, mono: true, render: (p) => p.circuitoDescricao },
  { chave: "cor", rotulo: "Cor", largura: 90, mono: true, render: (p) => p.corDescricao },
  { chave: "pino", rotulo: "Pino", largura: 70, mono: true, alinhamento: "direita", render: (p) => p.pino },
  { chave: "tipo", rotulo: "Tipo", largura: 140, render: (p) => <Tipo tipo={p.tipo} /> },
  { chave: "chicote", rotulo: "Chicote", largura: 110, render: (p) => p.chicoteDescricao },
  { chave: "restricoes", rotulo: "Restrições", largura: 150, render: (p) => <Restricoes lista={p.restricoes} /> },
  { chave: "obs", rotulo: "Observação", minimo: 130, render: (p) => p.obs },
  { chave: "arquivo", rotulo: "Fonte", largura: 110, mono: true, render: (p) => p.arquivoOriginal },
]

function Tipo({ tipo }: { tipo: number }) {
  const tom = tomDoTipo(tipo)
  const rotulo = rotuloTipoPinout(tipo)
  if (!tom) return <span style={{ color: "var(--txt2)" }}>{rotulo}</span>
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", height: 18, padding: "0 7px",
        borderRadius: 5, background: tom.fundo, color: tom.texto, fontSize: 10.5, fontWeight: 600,
      }}
    >
      {rotulo}
    </span>
  )
}

function Restricoes({ lista }: { lista: { id: number; descricao: string }[] }) {
  if (lista.length === 0) return null
  const primeira = lista[0]!
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        title={lista.map((r) => r.descricao).join(" · ")}
        style={{
          display: "inline-block", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", height: 18, lineHeight: "18px", padding: "0 7px", borderRadius: 5,
          background: "var(--surface3)", color: "var(--txt2)", fontSize: 10.5,
        }}
      >
        {primeira.descricao}
      </span>
      {lista.length > 1 && (
        <span style={{ fontFamily: "var(--fm)", fontSize: 10, color: "var(--txt3)" }}>+{lista.length - 1}</span>
      )}
    </span>
  )
}

// --------------------------------------------------------------- paginação

function Paginacao({
  pagina, paginas, carregando, aoIr,
}: {
  pagina: number
  paginas: number
  carregando: boolean
  aoIr: (p: number) => void
}) {
  const estilo = (ligado: boolean): React.CSSProperties => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 5,
    border: "1px solid var(--line2)", background: "var(--surface)",
    color: ligado ? "var(--txt2)" : "var(--txt3)",
    opacity: ligado ? 1 : 0.4, cursor: ligado ? "pointer" : "default",
  })

  return (
    <>
      {carregando && <span style={{ color: "var(--txt3)" }}>carregando…</span>}
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button disabled={pagina <= 1} onClick={() => aoIr(pagina - 1)} aria-label="Página anterior" style={estilo(pagina > 1)}>
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
            <path d="m14.5 6-6 6 6 6" />
          </svg>
        </button>
        <span className="num" style={{ minWidth: 62, textAlign: "center" }}>
          {pagina} de {paginas}
        </span>
        <button disabled={pagina >= paginas} onClick={() => aoIr(pagina + 1)} aria-label="Próxima página" style={estilo(pagina < paginas)}>
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
            <path d="m9.5 6 6 6-6 6" />
          </svg>
        </button>
      </span>
    </>
  )
}

/**
 * Baixa a planilha com os filtros que estão na tela.
 *
 * Levar os mesmos filtros é o ponto: exportar o projeto inteiro quando se está
 * olhando um componente é empurrar para a pessoa o trabalho de recortar depois.
 */
function BotaoDeExportar({ projetoId, filtro }: {
  projetoId: number
  filtro: ReturnType<typeof paraApi>
}) {
  const [ocupado, setOcupado] = useState<"xlsx" | "csv" | null>(null)
  const [erro, setErro] = useState(false)

  async function baixar(formato: "xlsx" | "csv") {
    setOcupado(formato)
    setErro(false)
    try {
      await exportarPinouts(projetoId, filtro, formato)
    } catch {
      setErro(true)
    } finally {
      setOcupado(null)
    }
  }

  return (
    <span
      title={erro ? "Não deu para exportar. Tente de novo." : "Baixa o que está filtrado"}
      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      <span style={{ fontSize: 11.5, color: erro ? "var(--erro)" : "var(--txt3)" }}>
        {erro ? "falhou" : "exportar"}
      </span>
      {(["xlsx", "csv"] as const).map((f) => (
        <button
          key={f}
          onClick={() => void baixar(f)}
          disabled={ocupado != null}
          style={{
            height: 26, padding: "0 9px", borderRadius: 6, border: "1px solid var(--line2)",
            background: "var(--surface)", fontSize: 11, fontWeight: 600, color: "var(--txt2)",
            opacity: ocupado != null ? 0.55 : 1,
          }}
        >
          {ocupado === f ? "…" : f.toUpperCase()}
        </button>
      ))}
    </span>
  )
}
